// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the phone menu that drops from what you pressed (JP 2026-10-25, design/media-viewer-menu
// concept A): a small frosted card next to the ⋯ or the held row, label on the left and icon on the
// right, instead of a sheet from the bottom. Nothing behind it moves; a held row is lifted out above
// the dim so you see what the menu is for. Takes the same groups as MenuBottomSheet. Lens/Glass
// material (JP 2026-10-27, design/waiting-row-menu B2): a frosted panel holding cards, and the main
// actions (quickIds) as a row of buttons on top with their labels under them. The document viewer
// can use a view toggle; long menus such as an agent chat can use the pills look and several quick rows.

import {useBottomSheetBackHandler} from '@app/features/app/hooks/useBottomSheetBackHandler';
import {isKeyboardActivationKey} from '@app/features/input/utils/KeyboardUtils';
import styles from '@app/features/ui/glass_menu/GlassMenu.module.css';
import {
	type Area,
	EDGE,
	type Placement,
	placeMenu,
	placePreviewMenu,
} from '@app/features/ui/glass_menu/GlassMenuPlacement';
import type {
	MenuCheckboxType,
	MenuGroupType,
	MenuItemType,
	MenuRadioType,
	MenuSheetItem,
	MenuSubmenuItemType,
} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {resolvePortalHost, usePortalHost} from '@app/features/ui/overlay/PortalHostContext';
import LayerManager from '@app/features/ui/state/LayerManager';
import OverlayStack from '@app/features/ui/state/OverlayStack';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconCheck, IconChevronLeft, IconChevronRight} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {type ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

const BACK_DESCRIPTOR = msg({
	message: 'Back',
	comment: 'Phone menu: row at the top of a submenu that goes back to the menu it came from.',
});
const MENU_DESCRIPTOR = msg({
	message: 'Options',
	comment: 'Phone menu: accessible name of a menu that has no title of its own.',
});

const OPEN_PREVIEW_DESCRIPTOR = msg({
	message: 'Open',
	comment: 'Phone menu: accessible name of the preview above a held chat or email, which opens it when tapped.',
});

const WIDTH = 252;
const PILLS_WIDTH = 366;

/** Where the menu comes from: the element pressed, or a point (a long press without a button). */
export type GlassMenuAnchor = HTMLElement | {x: number; y: number} | null;

/** A choice between a few views, shown as a segmented toggle on top of the menu. */
export interface GlassMenuToggle<T extends string = string> {
	/** Its accessible name, e.g. "Show the file as". */
	label: string;
	value: T;
	options: ReadonlyArray<{value: T; label: string; icon?: ReactNode}>;
	onChange: (value: T) => void;
}

export interface GlassMenuProps {
	isOpen: boolean;
	onClose: () => void;
	anchor: GlassMenuAnchor;
	groups: ReadonlyArray<MenuGroupType>;
	/** Over the media viewer the menu is always dark; elsewhere it follows the theme. */
	tone?: 'dark' | 'auto';
	/** Show a copy of the anchor raised above the dim (a long-pressed row). */
	lift?: boolean;
	/** Instead of the held row's copy, a read-only preview of what the row opens (a chat's latest messages). */
	preview?: ReactNode;
	/** Stays on top of the preview while its content scrolls (GlassMenuPreviewHead). */
	previewHead?: ReactNode;
	/** The preview opens at its top (an email) rather than its end (a chat's newest message). */
	previewFromTop?: boolean;
	/** Tapping the preview: it opens what it shows. */
	onPreviewOpen?: () => void;
	/** Items (actions or submenus) with these ids leave their group for the button row on top. */
	quickIds?: ReadonlyArray<string>;
	/** A segmented toggle on top (above the quick row); choosing closes the menu. */
	toggle?: GlassMenuToggle;
	/** As quickIds, in several rows (each row only shows the ids that are there). */
	quickRows?: ReadonlyArray<ReadonlyArray<string>>;
	/** `pills`: the bottom sheet's round pills and icon-first rows, wider. */
	look?: 'cards' | 'pills';
	/** The cards look's width when its rows carry counts and switches (Calendar's Show menu). */
	width?: number;
	title?: string;
	subtitle?: string;
	leading?: ReactNode;
	/** Something to act on before the menu (an agent's waiting approval), under the title. */
	header?: ReactNode;
	'data-flx'?: string;
}

const isSubmenu = (item: MenuSheetItem): item is MenuSubmenuItemType => 'items' in item;
const isAction = (item: MenuSheetItem): item is MenuItemType => 'onClick' in item;
const isCheckbox = (item: MenuSheetItem): item is MenuCheckboxType => 'checked' in item && 'onChange' in item;
const isRadio = (item: MenuSheetItem): item is MenuRadioType => 'selected' in item && 'onSelect' in item;

function anchorRect(anchor: GlassMenuAnchor): DOMRect | null {
	if (!anchor) return null;
	if (anchor instanceof HTMLElement) return anchor.isConnected ? anchor.getBoundingClientRect() : null;
	return new DOMRect(anchor.x, anchor.y, 0, 0);
}

/** The visible screen and its safe-area insets (status bar or island, home indicator), as CSS reads them. */
export function screenArea(): Area {
	const probe = document.createElement('div');
	probe.style.cssText =
		'position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom)';
	document.body.append(probe);
	const style = getComputedStyle(probe);
	const insetTop = Number.parseFloat(style.paddingTop) || 0;
	const insetBottom = Number.parseFloat(style.paddingBottom) || 0;
	probe.remove();
	const viewport = window.visualViewport;
	return {
		height: viewport ? viewport.height + viewport.offsetTop : window.innerHeight,
		width: window.innerWidth,
		insetTop,
		insetBottom,
	};
}

export function GlassMenu({
	isOpen,
	onClose,
	anchor,
	groups,
	tone = 'auto',
	lift = false,
	preview,
	previewHead,
	previewFromTop = false,
	onPreviewOpen,
	quickIds,
	toggle,
	quickRows,
	look = 'cards',
	width,
	title,
	subtitle,
	leading,
	header,
	'data-flx': flx = 'ui.glass-menu',
}: GlassMenuProps) {
	const {i18n} = useLingui();
	const portalHost = usePortalHost();
	const cardRef = useRef<HTMLDivElement | null>(null);
	const [placement, setPlacement] = useState<Placement | null>(null);
	const [lifted, setLifted] = useState<DOMRect | null>(null);
	const liftRef = useRef<HTMLDivElement | null>(null);
	const previewHeadRef = useRef<HTMLDivElement | null>(null);
	const previewRef = useRef<HTMLDivElement | null>(null);
	// The finger that opened the menu lifts over whatever opened under it. Only a press that began in
	// the open menu (or a key press, whose click has no detail) counts as a tap.
	const pressedInside = useRef(false);
	const hasPreview = preview != null;
	const [previewBox, setPreviewBox] = useState<{top: number; left: number; width: number; height: number} | null>(null);
	const [zIndex, setZIndex] = useState<number | null>(null);
	// The submenus opened, innermost last; the menu shows the last one.
	const [trail, setTrail] = useState<Array<MenuSubmenuItemType>>([]);

	useEffect(() => {
		if (!isOpen) return;
		setZIndex(OverlayStack.acquire());
		return () => {
			pressedInside.current = false;
			OverlayStack.release();
			setZIndex(null);
			setTrail([]);
			setPlacement(null);
			setPreviewBox(null);
			setLifted(null);
			setLiftShift(0);
		};
	}, [isOpen]);

	// Placed once open (and again when a submenu opens or the screen changes size); a held row that
	// must move to make room slides there after it first shows in place.
	const [liftShift, setLiftShift] = useState(0);
	const heldRect = useRef<DOMRect | null>(null);
	useLayoutEffect(() => {
		if (!isOpen || !cardRef.current) return;
		const rect = anchorRect(anchor);
		heldRect.current = rect;
		const held = lift && !!rect && anchor instanceof HTMLElement;
		const measure = () => {
			if (!cardRef.current) return;
			if (hasPreview && heldRect.current) {
				const area = screenArea();
				const next = placePreviewMenu(
					heldRect.current,
					(previewHeadRef.current?.offsetHeight ?? 0) + (previewRef.current?.offsetHeight ?? 0),
					cardRef.current.scrollHeight,
					width ?? WIDTH,
					area,
				);
				setPlacement(next);
				setPreviewBox({
					top: next.previewTop,
					left: EDGE,
					width: area.width - EDGE * 2,
					height: next.previewHeight,
				});
				return;
			}
			const next = placeMenu(
				heldRect.current,
				cardRef.current.scrollHeight,
				look === 'pills' ? PILLS_WIDTH : (width ?? WIDTH),
				screenArea(),
				held,
			);
			setPlacement(next);
			requestAnimationFrame(() => setLiftShift(next.liftShift));
		};
		measure();
		if (held && !hasPreview) setLifted(rect);
		window.visualViewport?.addEventListener('resize', measure);
		window.addEventListener('resize', measure);
		// A preview grows when its messages arrive; the menu moves down with it.
		const content = hasPreview ? new ResizeObserver(measure) : null;
		if (previewRef.current) content?.observe(previewRef.current);
		return () => {
			content?.disconnect();
			window.visualViewport?.removeEventListener('resize', measure);
			window.removeEventListener('resize', measure);
		};
	}, [anchor, hasPreview, isOpen, lift, look, trail, zIndex, width]);

	// The lifted copy of the held row: its own nodes, so it looks exactly like it.
	useLayoutEffect(() => {
		if (lifted && liftRef.current && anchor instanceof HTMLElement)
			liftRef.current.replaceChildren(anchor.cloneNode(true));
	}, [anchor, lifted]);

	// Escape closes the menu only: as the top layer it goes before what it opened over (the viewer).
	const layerKey = useId();
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;
	useEffect(() => {
		if (!isOpen) return;
		LayerManager.addLayer('contextmenu', layerKey, () => onCloseRef.current());
		return () => LayerManager.removeLayer('contextmenu', layerKey);
	}, [isOpen, layerKey]);

	useBottomSheetBackHandler(isOpen, onClose);

	if (!isOpen || zIndex === null || typeof document === 'undefined') return null;
	const submenu = trail.at(-1);
	const showHead = !submenu && (title || leading);
	return createPortal(
		<div
			className={clsx(styles.root, tone === 'dark' ? styles.dark : styles.auto)}
			style={{zIndex}}
			onPointerDownCapture={() => {
				pressedInside.current = true;
			}}
			onClickCapture={(event) => {
				if (event.detail !== 0 && !pressedInside.current) {
					event.preventDefault();
					event.stopPropagation();
				}
			}}
			data-flx={flx}
		>
			<div
				className={clsx(styles.dim, (lift || look === 'pills') && styles.dimLift)}
				onClick={onClose}
				aria-hidden="true"
			/>
			{hasPreview && (
				<div
					className={clsx(styles.preview, previewBox && styles.previewShown)}
					style={previewBox ?? undefined}
					role="button"
					tabIndex={0}
					aria-label={i18n._(OPEN_PREVIEW_DESCRIPTOR)}
					onClick={onPreviewOpen}
					onKeyDown={(event) => {
						if (!isKeyboardActivationKey(event.key)) return;
						event.preventDefault();
						onPreviewOpen?.();
					}}
					data-flx={`${flx}.preview`}
				>
					{previewHead && <div ref={previewHeadRef}>{previewHead}</div>}
					<div className={clsx(styles.previewScroll, previewFromTop && styles.previewScrollTop)}>
						<div ref={previewRef} inert>
							{preview}
						</div>
					</div>
				</div>
			)}
			{lifted && (
				<div
					ref={liftRef}
					className={styles.lifted}
					style={{top: lifted.top - 4 + liftShift, left: lifted.left - 4, width: lifted.width + 8}}
					aria-hidden="true"
				/>
			)}
			<div
				ref={cardRef}
				role="menu"
				aria-label={title ?? i18n._(MENU_DESCRIPTOR)}
				className={clsx(styles.card, look === 'pills' && styles.pills, placement && styles.cardShown)}
				style={
					placement
						? {
								top: placement.top,
								left: placement.left,
								...(width ? {width: `min(${width}px, calc(100vw - 24px))`} : {}),
								maxHeight: placement.maxHeight,
								transformOrigin: placement.origin,
							}
						: undefined
				}
				data-flx={`${flx}.card`}
			>
				{showHead && (
					<div className={styles.head}>
						{leading && (
							<span className={styles.leading} aria-hidden="true">
								{leading}
							</span>
						)}
						<span className={styles.headText}>
							{title && <span className={styles.title}>{title}</span>}
							{subtitle && <span className={styles.subtitle}>{subtitle}</span>}
						</span>
					</div>
				)}
				{header && !submenu && <div className={styles.header}>{header}</div>}
				{toggle && !submenu && (
					<div className={styles.toggle} role="group" aria-label={toggle.label}>
						{toggle.options.map((option) => (
							<button
								key={option.value}
								type="button"
								role="menuitemradio"
								aria-checked={toggle.value === option.value}
								className={clsx(styles.toggleItem, toggle.value === option.value && styles.toggleOn)}
								onClick={() => {
									toggle.onChange(option.value);
									onClose();
								}}
								data-flx={`${flx}.toggle`}
								data-value={option.value}
							>
								{option.icon && <span aria-hidden="true">{option.icon}</span>}
								<span>{option.label}</span>
							</button>
						))}
					</div>
				)}
				<GlassMenuItems
					groups={groups}
					quickRows={quickRows ?? (quickIds ? [quickIds] : [])}
					trail={trail}
					onTrail={setTrail}
					onClose={onClose}
					flx={flx}
				/>
			</div>
		</div>,
		resolvePortalHost(portalHost),
	);
}

/** A preview's fixed top: who or what it shows, and when. */
export function GlassMenuPreviewHead({
	avatar,
	title,
	subtitle,
	stamp,
}: {
	avatar: ReactNode;
	title: string;
	subtitle?: string;
	stamp?: string;
}) {
	return (
		<div className={styles.previewHead}>
			{avatar}
			<span className={styles.previewHeadText}>
				<span className={styles.previewTitle}>{title}</span>
				{subtitle && <span className={styles.previewSubtitle}>{subtitle}</span>}
			</span>
			{stamp && <span className={styles.previewStamp}>{stamp}</span>}
		</div>
	);
}

/** What a preview shows until its messages arrive (the row's own one line), and if they never do. */
export function GlassMenuPreviewText({children}: {children: ReactNode}) {
	return <div className={styles.previewText}>{children}</div>;
}

export interface GlassMenuItemsProps {
	groups: ReadonlyArray<MenuGroupType>;
	/** Rows of quick ids: those items leave their groups for buttons on top. */
	quickRows: ReadonlyArray<ReadonlyArray<string>>;
	/** The submenus opened, innermost last; the last one is shown with a Back row. */
	trail: ReadonlyArray<MenuSubmenuItemType>;
	onTrail: (trail: Array<MenuSubmenuItemType>) => void;
	onClose: () => void;
	flx: string;
}

/**
 * What a glass menu holds: the quick buttons, then the groups as cards (or an open submenu with a
 * Back row). GlassMenu places it next to what was pressed; GlassMessageMenu under a lifted message.
 */
export function GlassMenuItems({groups, quickRows, trail, onTrail, onClose, flx}: GlassMenuItemsProps) {
	const {i18n} = useLingui();
	const run = useCallback(
		(item: MenuItemType) => {
			if (item.disabled) return;
			if (item.closeOnSelect !== false) onClose();
			item.onClick();
		},
		[onClose],
	);

	const submenu = trail.at(-1);
	const rowsOfIds = quickRows;
	const allQuick = rowsOfIds.flat();
	const isQuick = (item: MenuSheetItem) => !submenu && 'id' in item && item.id != null && allQuick.includes(item.id);
	const quickRowsShown = submenu
		? []
		: rowsOfIds
				.map((ids) =>
					ids.flatMap((id) => groups.flatMap((group) => group.items).filter((item) => 'id' in item && item.id === id)),
				)
				.filter((row) => row.length > 0);
	const shownGroups: ReadonlyArray<MenuGroupType> = submenu
		? [{items: submenu.items}]
		: groups.map((group) => ({...group, items: group.items.filter((item) => !isQuick(item))}));
	const quickButton = (item: MenuSheetItem) => {
		if (!isAction(item) && !isSubmenu(item)) return null;
		const label = item.quickLabel || item.label;
		return (
			<button
				key={item.id ?? item.label}
				type="button"
				role="menuitem"
				aria-label={isAction(item) && item.subtext ? `${item.label}, ${item.subtext}` : item.label}
				className={clsx(styles.quick, isAction(item) && item.quickInk && styles.quickInk)}
				disabled={item.disabled}
				onClick={() => {
					if (isAction(item)) run(item);
					else {
						item.onTriggerSelect?.();
						onTrail([...trail, item]);
					}
				}}
				data-flx={`${flx}.quick`}
				data-id={item.id}
			>
				<span className={styles.quickButton} aria-hidden="true">
					{item.icon}
				</span>
				<span className={styles.quickLabel} aria-hidden="true">
					{label}
				</span>
			</button>
		);
	};
	const icon = (node: ReactNode) =>
		node ? (
			<span className={styles.icon} aria-hidden="true">
				{node}
			</span>
		) : null;
	const row = (item: MenuSheetItem, key: string) => {
		if (isSubmenu(item))
			return (
				<button
					key={key}
					type="button"
					role="menuitem"
					className={styles.row}
					disabled={item.disabled}
					onClick={() => {
						item.onTriggerSelect?.();
						onTrail([...trail, item]);
					}}
					data-flx={`${flx}.submenu`}
				>
					<span className={styles.label}>{item.label}</span>
					{item.hint && <span className={styles.hint}>{item.hint}</span>}
					<span className={styles.icon} aria-hidden="true">
						<IconChevronRight size={18} stroke={1.8} />
					</span>
				</button>
			);
		if (isAction(item))
			return (
				<button
					key={key}
					type="button"
					role="menuitem"
					className={clsx(styles.row, item.danger && styles.danger)}
					disabled={item.disabled}
					onClick={() => run(item)}
					data-flx={`${flx}.item`}
					data-id={item.id}
				>
					<span className={styles.label}>
						{item.label}
						{item.subtext && <span className={styles.subtext}>{item.subtext}</span>}
					</span>
					{icon(item.icon)}
				</button>
			);
		if (isCheckbox(item))
			return (
				<button
					key={key}
					type="button"
					role="menuitemcheckbox"
					aria-checked={item.checked}
					className={styles.row}
					disabled={item.disabled}
					onClick={() => item.onChange(!item.checked)}
					data-flx={`${flx}.checkbox`}
				>
					<span className={styles.label}>{item.label}</span>
					{item.trailing && <span className={clsx(styles.hint, styles.trailing)}>{item.trailing}</span>}
					{item.appearance === 'switch' ? (
						<span className={clsx(styles.switch, item.checked && styles.switchOn)} aria-hidden="true" />
					) : (
						<span className={styles.icon} aria-hidden="true">
							{item.checked ? <IconCheck size={18} stroke={2} /> : item.icon}
						</span>
					)}
				</button>
			);
		if (isRadio(item))
			return (
				<button
					key={key}
					type="button"
					role="menuitemradio"
					aria-checked={item.selected}
					className={styles.row}
					disabled={item.disabled}
					onClick={() => {
						item.onSelect();
						onClose();
					}}
					data-flx={`${flx}.radio`}
				>
					<span className={styles.label}>
						{item.label}
						{item.subtext && <span className={styles.subtext}>{item.subtext}</span>}
					</span>
					<span className={styles.icon} aria-hidden="true">
						{item.selected && <IconCheck size={18} stroke={2} />}
					</span>
				</button>
			);
		// Sliders have no place in a short menu.
		return null;
	};
	return (
		<>
			{quickRowsShown.map((row, index) => (
				<div key={index} className={styles.quickRow} role="group">
					{row.map(quickButton)}
				</div>
			))}
			{submenu && (
				<button
					type="button"
					className={clsx(styles.row, styles.back)}
					onClick={() => onTrail(trail.slice(0, -1))}
					data-flx={`${flx}.back`}
				>
					<span className={styles.icon} aria-hidden="true">
						<IconChevronLeft size={18} stroke={1.8} />
					</span>
					<span className={styles.label}>{submenu.label || i18n._(BACK_DESCRIPTOR)}</span>
				</button>
			)}
			{shownGroups
				.filter((group) => group.items.length > 0)
				.map((group, index) => (
					<div key={`${index}:${group.title ?? ''}`} className={styles.group} role="group">
						{group.title && (
							<div className={styles.groupTitle}>
								{group.titleIcon && <span className={styles.groupTitleIcon}>{group.titleIcon}</span>}
								{group.title}
							</div>
						)}
						{group.items.map((item, itemIndex) => row(item, `${index}:${itemIndex}`))}
					</div>
				))}
		</>
	);
}
